import React, { useState } from 'react'
import { Button } from '@chakra-ui/react'
import { Box, Text, HStack } from '@chakra-ui/react'

import {
    ClientInfo,
    ClientFieldsFragment,
    AppointmentFieldsFragment,
} from '~graphql/generated/graphql'
import SuggestedClients from './suggested-clients'
import SearchBar from './search-bar'
import SharedStack from '~components/shared/wrappers/shared-stack'
import { useDebounce } from '~utils/hooks/use-debounce'
import SearchResults from './search-results'
import { FaCheckCircle } from 'react-icons/fa'
import { Icon } from '@chakra-ui/icons'
import StepHeader from '../step-header'

interface Props {
    appointment: AppointmentFieldsFragment
    client: ClientFieldsFragment | undefined
    setClient: (client: ClientFieldsFragment) => void
    isMakingNewClient: boolean
    setIsMakingNewClient: (isMakingNewClient: boolean) => void
}

export default function ClientSelect({
    appointment,
    client,
    setClient,
    isMakingNewClient,
    setIsMakingNewClient,
}: Props) {
    const { client_info, patient_info } = appointment
    const [searchString, setSearchString] = useState('')
    const debouncedSearchString = useDebounce(searchString, 500)

    const isClientSelected = !!client || isMakingNewClient

    if (!client_info || !patient_info) {
        return <>No client or patient info found on linked appointment</>
    }

    return (
        <SharedStack spacing={4}>
            <StepHeader
                number={1}
                label="Select a client"
                isCompleted={isClientSelected}
                alternateAction={
                    <Text>
                        {'('}or{' '}
                        <Button
                            as="span"
                            cursor="pointer"
                            variant="link"
                            colorScheme="brand"
                            onClick={() => setIsMakingNewClient(true)}
                        >
                            Create a new one
                        </Button>
                        {')'}
                    </Text>
                }
            />
            {!isClientSelected && (
                <>
                    <SearchBar
                        value={searchString}
                        onChange={v => setSearchString(v)}
                    />
                    {debouncedSearchString ? (
                        <SearchResults
                            searchString={debouncedSearchString}
                            clientInfo={client_info}
                            patientInfo={patient_info}
                            setClient={setClient}
                        />
                    ) : (
                        <SuggestedClients
                            clientInfo={client_info}
                            patientInfo={patient_info}
                            setClient={setClient}
                        />
                    )}
                </>
            )}
        </SharedStack>
    )
}
